import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid,
  LinearProgress,
  MenuItem,
} from '@mui/material'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import { Form, Formik } from 'formik'
import * as yup from 'yup'

import {
  BRASIL_CODE,
  COMPANY_STATUS,
  COUNTRY_CODE_OPTIONS,
  MESSAGES,
  ROLES,
  splitPhoneNumberByCountryCode,
  UTILS,
} from '@/shared/constants'
import { hasActiveOfficialWhatsAppTransport } from '@/shared/whatsappIntegration'
import useWhatssetWhatsAppIntegrationsApi from '@/hooks/useWhatsappIntegrationsApi'
import { RootState } from '@/store'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setIsConnected } from '@/store/features/whatsappSlice'
import useWhatsappApi from '@/hooks/useWhatsAppApi'
import FormikTextField from '@/components/global/forms/FormikTextField'
import { setIsOfficialApiActivationOpen } from '@/store/features/whatsappSlice'

interface Props {
  open: boolean
  onClose: () => void
}

const validationSchema = yup.object({
  countryCode: yup.string().required(MESSAGES.REQUIRED_FIELD),
  cellPhone: yup
    .string()
    .matches(/^\d{7,12}$/, MESSAGES.INVALID_FORMAT)
    .required(MESSAGES.REQUIRED_FIELD),
})

export default function UpdateWhatsappNumberDialog(props: Props) {
  const { onClose, open } = props
  const { getStatus } = useWhatsappApi()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state: RootState) => state.auth)
  const {
    updateWhatsAppIntegrationNumber,
    getWhatsAppIntegrationNumber,
    cellPhone,
  } = useWhatssetWhatsAppIntegrationsApi()
  const [initialValues, setInitialValues] = useState({
    countryCode: BRASIL_CODE,
    cellPhone: UTILS.BLANK,
  })
  const integration = user?.company?.whatsappIntegration
  const hasActiveOfficialTransport =
    hasActiveOfficialWhatsAppTransport(integration)
  const shouldShowOfficialSignup =
    user?.role.id === ROLES.COMPANY_ADMIN &&
    user.company?.status !== COMPANY_STATUS.CANCELED &&
    !hasActiveOfficialTransport

  useEffect(() => {
    if (user?.company?.whatsappIntegration?.id) {
      getWhatsAppIntegrationNumber(
        user.company.whatsappIntegration.id.toString()
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    const { countryCode, cellPhone: localPhoneNumber } =
      splitPhoneNumberByCountryCode(cellPhone)

    setInitialValues({
      countryCode,
      cellPhone: localPhoneNumber,
    })
  }, [cellPhone])

  const handleSave = async (
    values: { countryCode: string; cellPhone: string },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    values.cellPhone = `${values.countryCode}${values.cellPhone}`

    const success = await updateWhatsAppIntegrationNumber(
      user!.company!.whatsappIntegration!.id.toString(),
      values.cellPhone
    )
    setSubmitting(false)

    if (success) {
      const status = await getStatus()
      if (status === true || status === false) {
        dispatch(setIsConnected(status))
      }

      handleClose()
    }
  }

  const handleClose = () => onClose()

  return (
    <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Número de conexão com WhatsApp</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) =>
          handleSave(values, setSubmitting)
        }
      >
        {({ submitForm, isSubmitting }) => (
          <Form
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submitForm()
              }
            }}
          >
            <DialogContent>
              {shouldShowOfficialSignup && (
                <Box display="grid" gap={2} mb={3}>
                  <Alert severity="info">
                    Conecte a API oficial do WhatsApp para liberar templates,
                    janelas de conversa e maior estabilidade no atendimento.
                  </Alert>
                  <Button variant="contained" onClick={() => { onClose(); dispatch(setIsOfficialApiActivationOpen(true)) }}>Conhecer a API oficial e estimar gastos</Button>
                </Box>
              )}
              <Grid
                container
                flexDirection={'row'}
                size={12}
                spacing={3}
                pt={1}
              >
                <Grid size={4}>
                  <FormikTextField
                    select={true}
                    fullWidth
                    label="País"
                    name="countryCode"
                    slotProps={{
                      htmlInput: {
                        id: 'countryCode',
                      },
                    }}
                  >
                    <MenuItem value={UTILS.BLANK}>{UTILS.DASH}</MenuItem>
                    {COUNTRY_CODE_OPTIONS.map((option) => (
                      <MenuItem
                        key={`${option.value}-${option.label}`}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid size={8}>
                  <FormikTextField
                    fullWidth
                    type="text"
                    label="WhatsApp"
                    name="cellPhone"
                  />
                  <FormHelperText>DDD + 8 dígitos (6288888888)</FormHelperText>
                </Grid>
                <Grid
                  size={{ md: 8 }}
                  sx={{ display: { xs: 'none', md: 'block' } }}
                />
                {isSubmitting && (
                  <Grid size={12}>
                    <LinearProgress />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={submitForm}
              >
                Salvar
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
